import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetBrandMenusMutation } from "redux/menu/service";
import { UTILS } from "utils";
import useAllStores from "hooks/useCurrentUserStores";
import OrderingPagePreview from "features/menus/components/OrderingPagePreview";
import AppContainer from "components/ui/AppContainer.jsx";
import AppButton from "components/ui/AppButton";

function currentStoreIdFromSession(storeOptions) {
  if (!storeOptions?.length) return null;
  try {
    const sid = localStorage.getItem("storeId");
    if (
      sid &&
      storeOptions.some((o) => String(o.value) === String(sid))
    ) {
      return sid;
    }
  } catch (e) {
    /* ignore */
  }
  return storeOptions[0].value;
}

const PreviewStore = ({ onSaveAndContinue }) => {
  const { t } = useTranslation();
  const { stores: storeOptions, loading: storesLoading } = useAllStores();
  const [getBrandMenus] = useGetBrandMenusMutation();
  const [menuId, setMenuId] = useState(null);
  const [menusLoading, setMenusLoading] = useState(true);
  const [currentStoreId, setCurrentStoreId] = useState(null);
  const [random, setRandom] = useState(0);

  const updateState = useCallback((key, value) => {
    if (key === "random") setRandom(value);
  }, []);

  const menuPreviewState = useMemo(() => ({ random }), [random]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMenusLoading(true);
      try {
        const userDetail = UTILS.getCurrentUser();
        const { data: payload } = await getBrandMenus({
          brandId: userDetail.brandId,
        });
        if (
          cancelled ||
          payload?.success !== 1 ||
          !Array.isArray(payload?.data)
        ) {
          if (!cancelled) setMenuId(null);
          return;
        }
        const list = payload.data;
        const defaultMenu = list.find((m) => m.isDefault) || list[0];
        setMenuId(defaultMenu?._id ?? null);
      } catch (e) {
        if (!cancelled) setMenuId(null);
      } finally {
        if (!cancelled) setMenusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getBrandMenus]);

  useEffect(() => {
    if (storesLoading || !storeOptions?.length) return;
    setCurrentStoreId(currentStoreIdFromSession(storeOptions));
  }, [storesLoading, storeOptions]);

  const showPreview =
    !menusLoading && menuId && currentStoreId != null && !storesLoading;
  const footer = (
    <div className="flex justify-end">
      <AppButton onClick={() => onSaveAndContinue?.()} disabled>
        {t("Completed")}
      </AppButton>
    </div>
  );

  return (
    <AppContainer
      title={t("Preview Store")}
      subTitle={t("preview_store_ordering_widget_hint")}
      loading={menusLoading || storesLoading}
      footer={footer}
    >
      {showPreview ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <OrderingPagePreview
            variant="store"
            state={menuPreviewState}
            updateState={updateState}
            menuId={menuId}
            storeId={currentStoreId}
          />
        </div>
      ) : null}

      {!storesLoading && !storeOptions?.length ? (
        <p className="text-sm text-gray-500">{t("preview_store_no_stores")}</p>
      ) : null}
      {!menusLoading && !menuId ? (
        <p className="text-sm text-gray-500">{t("preview_store_no_menus")}</p>
      ) : null}
    </AppContainer>
  );
};

export default PreviewStore;
